import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { namespace } from '@common/config/constants';
import {
    Copy,
    Trash2,
    ArrowRight,
    Plus,
    CornerDownRight,
    ListTree,
    Link,
    Text,
    MoreHorizontal,
    Maximize2,
    ChevronRight,
    CornerRightUp
} from 'lucide-react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { usePortalContainer } from '@/common/hooks/use-portal-container';

import { BoardRowType } from "@common-models/task";
import { bulkActions, getBulkTaskList } from '@common-reducers/BulkActionsReducer';
import { DBTasksThunks, tasksSelectByQuery, tasksSelectors } from "@common-reducers/DBServiceThunks";
import { setExtraDataOfTaskId } from '@common-reducers/ExtraDataReducer';
import { mainSliceActions } from '@common-reducers/MainReducer';
import { getSubTasksCount } from '@common-reducers/TaskSelectors';
import { doesTaskBelongToAudienceBoard } from '@common-reducers/BoardsSelectors';
import { addNewTaskThunk, convertSubTaskToTaskAndReOrderGroup, moveTaskToTopOfTheGroupAndReOrderRestOfTasks } from '@common-reducers/TaskThunks';
import { taskActions } from '@common-reducers/TasksReducer';
import { UndoRedoAction } from '@common-reducers/UndoRedoReducer';
import { doActionThunk } from '@common-reducers/UndoRedoThunks';
import { isExtensionContextOrWhatsappView } from "@common-services/detect-context";

import PromptDeleteAction from '@common-components/prompt-delete-action/prompt-delete-action';
import MoveToDialog from '@common-components/tasks-panel/bulk-actions-dialog/move-to/move-to-dialog';
import SelectParentTask from './select-parent-task/select-parent-task';

interface TaskMenuProps {
    taskId: string;
}

export default function TaskMenu({ taskId }: TaskMenuProps) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);
    const [deletePromptOpen, setDeletePromptOpen] = useState(false);

    // Select task data locally using prop
    const task = useAppSelector((state) => tasksSelectors.selectById(state, taskId));
    const hasSubs = useAppSelector((state) => getSubTasksCount(state, taskId));
    // An audience is a board under the hood: its rows are contacts, not tasks.
    const isAudienceRow = useAppSelector((state) => doesTaskBelongToAudienceBoard(state, taskId));
    
    // Global states (careful with these)
    const displayPanel = useAppSelector((state) => state.TasksReducer.currentBoardView);
    const move_to = useAppSelector((state) => state.BulkActionsReducer.move_to);
    const bulkTasksList = useAppSelector(getBulkTaskList);

    const isSubTask = task?.parentTaskId ? true : false;


    useEffect(() => {
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    if (!task) return null;

    // --- Handlers ---

    const openExtraDataPanel = () => {
        dispatch(setExtraDataOfTaskId(task._id));
        dispatch(taskActions.setTaskViewSideDrawerOpenWith("ExtraDataPanel"));
        // dispatch(taskActions.toggleTaskMenu('')); // No longer needed with Shadcn
    };

    const duplicateItem = () => {
        const clonedTask = { ...task };
        delete clonedTask._id;
        clonedTask.text = `${clonedTask.text} - (copy)`;
        delete clonedTask.order;
        dispatch(addNewTaskThunk(clonedTask));
    };

    const copyLinkToTask = () => {
        const url = `${window.location.origin}/tasks?boardID=${task.boardId}&taskId=${task._id}`;
        navigator.clipboard.writeText(url);
        dispatch(taskActions.setTaskSnackbarMessage(t('taskMenu.copyTaskLinkToClipboard')));
    };

    const createNewTaskBelowItem = async () => {
        const newTask = {
            text: t('taskMenu.newItem'),
            boardId: task?.boardId,
            groupId: task?.groupId,
            status: "",
            priority: "",
            person: "",
            order: task?.order
        };
        const res = await dispatch(DBTasksThunks.create(newTask)).unwrap();
        dispatch(taskActions.setForceEditModeByTaskid(res._id?.toString() ?? ""));
    };

    const addSubItem = async () => {
        const newTask = {
            text: t('taskMenu.newSubitem'),
            boardId: task?.boardId,
            groupId: task?.groupId,
            status: "",
            priority: "",
            person: "",
            order: task?.order,
            parentTaskId: task?._id,
            taskType: BoardRowType.subTask
        };
        dispatch(DBTasksThunks.patch({
            entity: { ...task, showSubTasks: true }
        }));
        dispatch(taskActions.toggleTask(task?._id));
        const res = await dispatch(DBTasksThunks.create(newTask)).unwrap();
        dispatch(taskActions.setForceEditModeByTaskid(res._id?.toString() ?? ""));
    };

    const handleDeleteClick = () => {
        setDeletePromptOpen(true);
    };

    const handleDeleteConfirmationClosed = (shouldDelete?: boolean) => {
        if (shouldDelete === true) {
            const action: UndoRedoAction<string[], string> = {
                type: 'delete-task-list',
                data: [taskId],
                deletedOrArchived: "deleted",
                itemLabel: isAudienceRow ? 'contact' : undefined,
            };
            dispatch(doActionThunk({ action }));
        }
        setDeletePromptOpen(false);
    };

    // Move To Handlers
    const handleMoveToBoardStart = (e: React.MouseEvent) => {
        e.preventDefault();
        dispatch(bulkActions.setBulActionsMoveToId(taskId));
        dispatch(bulkActions.setGroupIsMoving(false));
        dispatch(bulkActions.setMovingTo('board'));
    };

    const handleMoveToGroupStart = (e: React.MouseEvent) => {
        e.preventDefault();
        dispatch(bulkActions.setBulActionsMoveToId(taskId));
        dispatch(bulkActions.setGroupIsMoving(false));
        dispatch(bulkActions.setMovingTo('group'));
    };

    const moveTaskToTopOfGroup = () => {
        // We might need to ensure the task is "selected" or "active" in the reducer for this thunk 
        // if it relies on 'menuActiveTaskId'. 
        // Let's check imports: `moveTaskToTopOfTheGroupAndReOrderRestOfTasks` usually uses state.
        dispatch(taskActions.toggleTaskMenu(taskId)); // Set active ID for the thunk
        dispatch(moveTaskToTopOfTheGroupAndReOrderRestOfTasks());
    };

    const convertSubTaskToTask = () => {
        if (!isSubTask) return;
        dispatch(convertSubTaskToTaskAndReOrderGroup()); // Check if this needs active ID
        dispatch(taskActions.closeSubtasksForTaskList([task.parentTaskId]));
        dispatch(bulkActions.setBulkTasksMap({}));
    };
    
    // Ensure active ID is set for complex thunks that might read it
    const onOpenChange = (open: boolean) => {
        if (open) {
            dispatch(taskActions.toggleTaskMenu(taskId));
        } else {
             // Reset move_to state on close
             if (move_to) dispatch(bulkActions.setMovingTo(''));
        }
    };

    return (
        <>
            <PromptDeleteAction 
                title={isAudienceRow ? t('taskMenu.promptDeleteContactTitle') : t('taskMenu.promptDeleteActionTitle')}
                handleClose={handleDeleteConfirmationClosed} 
                open={deletePromptOpen} 
            />

            <DropdownMenu onOpenChange={onOpenChange}>
                <DropdownMenuTrigger asChild>
                     <button className="p-1 rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors bg-transparent border-none cursor-pointer outline-none flex items-center justify-center">
                        <MoreHorizontal size={20} />
                    </button>
                </DropdownMenuTrigger>
                
                <DropdownMenuContent 
                    align="start" 
                    container={portalContainer || localPortalContainer}
                    className="w-64 z-[1400]" 
                >
                    {/* View / Edit */}
                    <DropdownMenuItem onClick={openExtraDataPanel}>
                        <Maximize2 className="mr-2 h-4 w-4" />
                        <span>{t('taskMenu.openButtonText')}</span>
                    </DropdownMenuItem>
                    
                    {/* Move To Submenu */}
                     <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                            <ArrowRight className="mr-2 h-4 w-4" />
                            <span>{t('taskMenu.moveToButtonText')}</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent container={portalContainer || localPortalContainer} className="w-56 z-[1405]">
                           {!move_to && (
                               <>
                                   <DropdownMenuItem onClick={handleMoveToBoardStart}>
                                       <span>{t('taskMenu.moveToBoardButtonText')}</span>
                                   </DropdownMenuItem>
                                   <DropdownMenuItem onClick={handleMoveToGroupStart}>
                                       <span>{t('taskMenu.moveToGroupButtonText')}</span>
                                   </DropdownMenuItem>
                                   {(!isSubTask && bulkTasksList.length === 1) && (
                                        <DropdownMenuItem onClick={moveTaskToTopOfGroup}>
                                            <span>Move to top of group</span> 
                                            {/* Note: Original label was t('taskMenu.moveToBoardButtonText') which seems wrong for "Top of Group" */}
                                        </DropdownMenuItem>
                                   )}
                               </>
                           )}
                           {move_to && (
                               <div className="p-2" onKeyDown={(e) => e.stopPropagation()}> 
                                    <MoveToDialog />
                               </div>
                           )}
                        </DropdownMenuSubContent>
                    </DropdownMenuSub>

                    <DropdownMenuItem onClick={duplicateItem}>
                        <Copy className="mr-2 h-4 w-4" />
                        <span>{t('taskMenu.duplicateButtonText')}</span>
                    </DropdownMenuItem>

                     {!isExtensionContextOrWhatsappView() && (
                        <DropdownMenuItem onClick={copyLinkToTask}>
                            <Link className="mr-2 h-4 w-4" />
                            <span>{t('taskMenu.copyTaskUrlButtonText')}</span>
                        </DropdownMenuItem>
                     )}
                     
                    <CopyToClipboard
                        text={task?.text}
                        onCopy={() => dispatch(taskActions.setTaskSnackbarMessage(t('taskMenu.copyToClipboardConfirmation')))}
                    >
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}> 
                            {/* Prevent default to keep menu open or allow clipboard action? 
                                Actually CopyToClipboard wraps the item. 
                                Shadcn MenuItem triggers onClick. 
                                We might need to wrap the whole MenuItem or put CopyToClipboard inside.
                            */}
                            <Text className="mr-2 h-4 w-4" />
                            <span>{t('taskMenu.copyNameButtonText')}</span>
                        </DropdownMenuItem>
                    </CopyToClipboard>

                    <DropdownMenuSeparator />

                    {/* Subitems */}
                    {!isSubTask && displayPanel.type === 'board' && (
                        <DropdownMenuItem onClick={addSubItem}>
                            <ListTree className="mr-2 h-4 w-4" />
                            <span>{t('taskMenu.addSubitemButtonText')}</span>
                        </DropdownMenuItem>
                    )}

                    {/* Convert To... with Tooltip/Disable check */}
                     <DropdownMenuItem 
                        disabled={hasSubs > 0}
                        onClick={isSubTask ? convertSubTaskToTask : undefined}
                         className={hasSubs > 0 ? "opacity-50 cursor-not-allowed" : ""}
                     >
                        {isSubTask ? <CornerRightUp className="mr-2 h-4 w-4" /> : <CornerDownRight className="mr-2 h-4 w-4" />}
                        <span>
                            {t('taskMenu.convertTo')}{" "}
                            {isSubTask ? t('taskMenu.convertToItemButtonText') : t('taskMenu.convertToSubitemButtonText')}
                        </span>
                        {/* If not subtask and no subs, we show SelectParentTask... this is intricate in a dropdown. 
                            Original logic: if !isSubTask and !hasSubs, show SelectParentTask box.
                            This was a layout inside the menu. 
                            In Dropdown, we can render custom content.
                        */}
                    </DropdownMenuItem>
                    
                     {!isSubTask && !hasSubs && (
                        <div className="p-2 pl-8">
                             {/* Styling to indent or separate */}
                             <SelectParentTask taskId={taskId} />
                        </div>
                    )}

                    <DropdownMenuSeparator />

                     {displayPanel.type === 'board' && (
                        <DropdownMenuItem onClick={createNewTaskBelowItem}>
                            <Plus className="mr-2 h-4 w-4" />
                            <span>{t('taskMenu.createNewItemBelowButtonText')}</span>
                        </DropdownMenuItem>
                    )}

                    <DropdownMenuItem onClick={handleDeleteClick} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>{t('taskMenu.deleteButtonText')}</span>
                    </DropdownMenuItem>

                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
}



























